<template>
  <ValidationObserver ref="observer" class="w-100" tag="form">
    <div class="pb-5">
      <div class="row">
        <div class="col-12 mt-4">
          <section-title v-if="showTitle" content="PEI Inicial - Ubicación" :has-line="true" />
        </div>
      </div>

      <div class="row ml-2">
        <div class="col-md-4 col-sm-12 mt-3">
          <ValidationProvider
            v-slot="{ errors }"
            name="Pei Inicial"
            rules="required"
          >
            <custom-date-picker
              id="txtGeneralCloseDate"
              v-model="value.peiInitialDate"
              required
              :disabled="!canEditPeiDate || readOnly"
              label="Fecha Inicial del PEI: "
              placeholder="DD/MM/YYYY"
              :error="errors[0]"
              :custom-error-msg.sync="resources['0675']"
            />
          </ValidationProvider>
        </div>
        <div class="col-md-4 col-sm-12 mt-3">
          <ValidationProvider v-slot="{ errors }" name="Fecha" rules="required">
            <custom-date-picker
              id="txtGeneralClosedDate"
              v-model="value.postAlternatePlacementDate"
              required
              :disabled="readOnly"
              label="Ubicación Inicial: "
              placeholder="DD/MM/YYYY"
              :max-date="new Date()"
              :min-date="value.eligibilityDeterminationDate"
              :error="errors[0]"
              :custom-error-msg.sync="resources['R03200150']"
            />
          </ValidationProvider>
        </div>
        <div class="col-md-4 col-sm-12 mt-3">
          <ValidationProvider
            v-slot="{ errors }"
            name="Alternativa de ubicacion"
            rules="required"
          >
            <custom-drop-down
              id="ddlPeiAlternatePlacement"
              v-model="value.peiAlternatePlacement"
              :disabled="!canEditPlacement || readOnly"
              fieldname="name"
              fieldkey="id"
              placeholder="Seleccione"
              label="Ubicación: "
              :options="conversionPeiAlternatePlacement"
              :error="errors[0]"
              :custom-error-msg="resources['0675']"
            />
          </ValidationProvider>
        </div>
      </div>
      <div class="col-12 mt-4 ml-2">
        <ValidationProvider
          v-slot="{ errors }"
          name="Comentario de elegibilidad"
          rules="required"
        >
          <custom-text-area
            id="txtPlacementDescription"
            v-model="value.postAlternatePlacementDescription"
            required
            :disabled="readOnly"
            placeholder="Escribir comentario"
            show-char-count
            :maxlength="500"
            :error="errors[0]"
            :custom-error-msg.sync="resources['R03200151']"
          />
        </ValidationProvider>
      </div>
    </div>
  </ValidationObserver>
</template>
<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { SectionTitle } from '@/components/pei/shared';
import { CustomDatePicker } from '@/components/common';
import CustomDropDown from '@/components/common/CustomDropDown';
import CustomTextArea from '@/components/common/CustomTextArea';
import alert from '@/mixin/alert.mixin';
import resourcesService from '@/services/resources.js';
import StudentStatuses from "@/utils/constants/student/studentStatuses.js";

export default {
  name: 'PeiInitialLocation',
  mixins: [alert],
  components: {
    ValidationObserver,
    ValidationProvider,
    SectionTitle,
    CustomDatePicker,
    CustomDropDown,
    CustomTextArea,
  },
  props: {
    studentData: {
      type: Object,
      required: true,
      default: () => {},
    },
    dateEligibility: {
      type: [Date, String],
      required: true,
      default: () => null,
    },
    value: {
      type: Object,
      required: true,
      default: () => {},
    },
    postRegisterLocationReadOnly: {
      type: Boolean,
      default: false,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      peiAndPlacementPostRegistry: {
        generalClosedDate: null,
        peiAlternatePlacement: null,
      },
      conversionPeiAlternatePlacement: [],
      resources: [],
    };
  },
  computed: {
    peiAlternatePlacement() {
      return this.value.peiAlternatePlacement;
    },
    isEligibleStudent(){
      return this.studentData.studentStatus == StudentStatuses.Elegible;
    },
    canEditPeiDate(){
      return (this.value.peiInitialDate == undefined && this.studentData.studentStatus !== StudentStatuses.Elegible) || 
            !this.isEligibleStudent;
    },
    canEditPlacement(){
      return (this.value.peiAlternatePlacement == undefined || 
            (this.value.peiAlternatePlacement != undefined && this.value.peiAlternatePlacement.id == undefined) &&
            this.studentData.studentStatus !== StudentStatuses.Elegible) || 
            !this.isEligibleStudent;
    },
  },
  watch: {
    async peiAlternatePlacement(newValue) {
      if (newValue) {
        await this.getAllConversionPeiAlternatePlacement();
      }
    },
  },
  methods: {
    // Get all conversion pei alternate placement from API.
    async getAllConversionPeiAlternatePlacement() {
      const self = this;
      const alternatePlacement = self.value.peiAlternatePlacement;

      let addPeiAlternatePlacementException = this.$store.getters.HasPermission(
        'registry.enableplacementcontrols'
      );

      let url = `/api/studentregistry/allConversionPeiAlternatePlacement/${addPeiAlternatePlacementException}`;
      if (
        alternatePlacement != undefined &&
        alternatePlacement.id != undefined
      ) {
        url = `/api/studentregistry/allConversionPeiAlternatePlacement/${addPeiAlternatePlacementException}?placementCode=${alternatePlacement.id}`;
      }

      const { data: response } = await this.$ApiGet(url).catch(() => {
        self.ShowToast('Error', self.resources['R03200108'], 'error');
      });

      this.conversionPeiAlternatePlacement = response;

      return response;
    },
    // Get pei and placement post registry.
    async getPeiAndPlacementPostRegistry() {
      const self = this;
      if (self.dateEligibility == null) return;

      const { data: response } = await this.$ApiGet(
        `/api/studentregistry/getPeiAndPlacementPostRegistry/${
          self.studentData.studentSieId
        }/${self.dateEligibility.toISOString()}`
      ).catch(() => {
        self.ShowToast('Error', self.resources['R03200108'], 'error');
      });

      if (response && response.peiAlternatePlacement >= 0) {
        this.value.peiAlternatePlacement = {
          id: response.peiAlternatePlacementCode,
          name: response.peiAlternatePlacementDesc,
        };
      }

      return response;
    },
    async loadResources() {
      this.resources = await resourcesService.LoadValidationMessages([
        'R03200108',
        'R03200150',
        'R03200151',
        '0675',
      ]);
    },
  },
  async mounted() {
    await this.getPeiAndPlacementPostRegistry();
    this.$emit('getRef', 'peiinitiallocation', this.$refs.observer);
    await this.loadResources();
    await this.getAllConversionPeiAlternatePlacement();
  },
};
</script>
